'use client'

/**
 * Rescues auth links that land on the wrong page.
 *
 * When a Supabase redirect target is not on the project's Redirect URL
 * allow-list, Supabase silently discards it and sends the user to SITE_URL
 * instead. The session still arrives, but in the URL hash fragment
 * (#access_token=...&type=recovery), which the server never sees. The result
 * is a password reset link that drops the user on the homepage with no
 * indication anything happened.
 *
 * This component watches for that fragment anywhere in the app, establishes
 * the session, and forwards the user to the page the link was meant to reach.
 * It also surfaces the error fragment Supabase returns for an expired link,
 * which otherwise vanishes just as silently.
 *
 * Fixing the allow-list is still the right thing to do; this makes the flow
 * work either way, and keeps working if a future template changes shape.
 */

import { useEffect } from 'react'
import { useRouter } from 'next/navigation'

/** Where each link type should end up once the session is established. */
const DESTINATION: Record<string, string> = {
  recovery: '/update-password',
  invite: '/update-password',
  signup: '/dashboard',
  magiclink: '/dashboard',
  email_change: '/dashboard/settings',
}

export function RecoveryLinkHandler() {
  const router = useRouter()

  useEffect(() => {
    if (typeof window === 'undefined') return
    const hash = window.location.hash
    if (!hash || hash.length < 2) return

    const params = new URLSearchParams(hash.slice(1))
    const errorCode = params.get('error') || params.get('error_code')
    const accessToken = params.get('access_token')
    const refreshToken = params.get('refresh_token')
    const type = params.get('type') ?? ''

    // nothing auth-shaped in the fragment: leave the page alone
    if (!errorCode && !accessToken) return

    // clear the fragment immediately so tokens are not left in the address bar,
    // in history, or in anything that later reads location.href
    const clean = window.location.pathname + window.location.search
    window.history.replaceState(null, '', clean)

    if (errorCode) {
      const description = params.get('error_description') || 'That link has expired or was already used. Please request a new one.'
      router.replace(`/login?error=${encodeURIComponent(errorCode)}&message=${encodeURIComponent(description.replace(/\+/g, ' '))}`)
      return
    }

    if (!accessToken || !refreshToken) return

    let cancelled = false
    ;(async () => {
      try {
        // Loaded only when a link actually arrives: this runs on every page,
        // including the marketing site, which otherwise never needs Supabase.
        const { createClient } = await import('@/lib/supabase/client')
        const supabase = createClient()
        const { error } = await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
        if (cancelled) return
        if (error) {
          router.replace(`/login?error=session_error&message=${encodeURIComponent('We could not open that link. Please request a new one.')}`)
          return
        }
        router.replace(DESTINATION[type] ?? '/dashboard')
      } catch {
        if (!cancelled) {
          router.replace(`/login?error=session_error&message=${encodeURIComponent('We could not open that link. Please request a new one.')}`)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [router])

  return null
}

export default RecoveryLinkHandler
